import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Globe, 
  Building2, 
  Type, 
  FileText,
  Palette,
  Save,
  Eye,
  Plus,
  Sparkles
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const NewSite = () => {
  const navigate = useNavigate()
  const [selectedTemplate, setSelectedTemplate] = useState('blank')
  
  const { register, handleSubmit, watch, formState: { errors } } = useForm()
  
  const siteName = watch('name', '')

  // Fetch clients for the dropdown
  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    'clients',
    () => apiService.get('/clients').then(res => res.data)
  )
  
  const clients = clientsData?.clients || []

  // Create site mutation
  const createSiteMutation = useMutation(
    (siteData) => apiService.post('/sites', siteData),
    {
      onSuccess: (response) => {
        toast.success('Site created successfully!')
        const siteId = response.data.id
        navigate(`/admin/editor?site=${siteId}`)
      },
      onError: (error) => {
        console.error('Site creation error:', error.response?.data)
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.errors?.[0]?.msg || 
                           'Failed to create site'
        toast.error(errorMessage)
      }
    }
  )

  const onSubmit = (data) => {
    const siteData = {
      name: data.name,
      client_id: data.client_id,
      template_id: null, // Always use null for now since templates aren't configured
      description: data.description || ''
    }
    
    createSiteMutation.mutate(siteData)
  }

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  }

  const templates = [
    {
      id: 'blank',
      name: 'Blank Site',
      description: 'Start with a clean slate',
      preview: '/api/templates/blank/preview.jpg',
      category: 'Basic'
    },
    {
      id: 'business',
      name: 'Business Website',
      description: 'Professional business template',
      preview: '/api/templates/business/preview.jpg',
      category: 'Business'
    },
    {
      id: 'portfolio',
      name: 'Portfolio',
      description: 'Showcase your work',
      preview: '/api/templates/portfolio/preview.jpg',
      category: 'Creative'
    },
    {
      id: 'landing',
      name: 'Landing Page',
      description: 'Single page marketing site',
      preview: '/api/templates/landing/preview.jpg',
      category: 'Marketing'
    }
  ]

  if (clientsLoading) {
    return (
      <div className="pt-4">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading clients..." />
        </div>
      </div>
    )
  }

  return (
    <div className="pt-4 space-y-8 animate-fade-in-up">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/sites')}
            className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
              Create New Site
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Set up a new website for your client
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Site Configuration */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2 space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-accent-100 dark:bg-accent-900/20 rounded-lg">
                  <Globe className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                </div>
                Site Information
              </CardTitle>
              <CardDescription>
                Configure the basic details for your new website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Site Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: 'Site name is required' })}
                    className={cn(
                      'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                      'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                      'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                      'transition-all duration-200',
                      errors.name && 'border-error-500 focus:ring-error-500'
                    )}
                    placeholder="Enter site name"
                  />
                  {errors.name && (
                    <p className="text-error-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                  {siteName && (
                    <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-2 bg-neutral-50 dark:bg-[#2f2b41] px-3 py-2 rounded-md">
                      <strong>URL slug:</strong> {generateSlug(siteName)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Client <span className="text-error-500">*</span>
                  </label>
                  <select
                    {...register('client_id', { required: 'Please select a client' })}
                    className={cn(
                      'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                      'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                      'transition-all duration-200',
                      errors.client_id && 'border-error-500 focus:ring-error-500'
                    )}
                  >
                    <option value="">Select a client</option>
                    {clients?.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.company_name}
                      </option>
                    ))}
                  </select>
                  {errors.client_id && (
                    <p className="text-error-500 text-sm mt-1">{errors.client_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={4}
                    className={cn(
                      'w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
                      'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                      'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
                      'transition-all duration-200 resize-none'
                    )}
                    placeholder="Optional description for this site (helps organize multiple sites for the same client)"
                  />
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Template Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                  <Palette className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                Choose Template
              </CardTitle>
              <CardDescription>
                Select a starting template for your website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {templates.map((template) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={cn(
                      "relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200",
                      selectedTemplate === template.id
                        ? "border-accent-500 bg-accent-50 dark:bg-accent-900/20 shadow-lg"
                        : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white dark:bg-dark-200"
                    )}
                  >
                    <div className="aspect-video bg-neutral-100 dark:bg-neutral-800 rounded-lg mb-3 flex items-center justify-center">
                      <FileText className="h-8 w-8 text-neutral-400" />
                    </div>
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                      {template.name}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {template.description}
                    </p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                        {template.category}
                      </span>
                    </div>
                    {selectedTemplate === template.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Preview and Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-success-100 dark:bg-success-900/20 rounded-lg">
                  <Eye className="h-5 w-5 text-success-600 dark:text-success-400" />
                </div>
                Preview & Create
              </CardTitle>
              <CardDescription>
                Review and create your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template Preview */}
              <div>
                <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
                  Selected Template
                </h4>
                <div className="aspect-video bg-neutral-100 dark:bg-[#2f2b41] rounded-lg border-2 border-dashed border-neutral-300 dark:border-[#3a3650] flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-neutral-400 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {templates.find(t => t.id === selectedTemplate)?.name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Site Summary */}
              {siteName && (
                <div className="space-y-3 p-4 bg-accent-50 dark:bg-accent-900/20 rounded-md border border-accent-200 dark:border-accent-800">
                  <h4 className="text-sm font-semibold text-accent-800 dark:text-accent-200">
                    Site Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-600 dark:text-neutral-400">Name:</span>
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">{siteName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600 dark:text-neutral-400">Slug:</span>
                      <span className="font-mono font-medium text-neutral-900 dark:text-neutral-100">/{generateSlug(siteName)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600 dark:text-neutral-400">Template:</span>
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">
                        {templates.find(t => t.id === selectedTemplate)?.name}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <Button
                  onClick={handleSubmit(onSubmit)}
                  disabled={createSiteMutation.isLoading}
                  className="w-full"
                  size="lg"
                >
                  {createSiteMutation.isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating Site...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Site
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/sites')}
                  className="w-full border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default NewSite